/**
 * AI Service detail page — `/services/:id` (task 380; restructured in task 800).
 *
 * The single home for everything a service owns, in seven quiet tabs (no
 * counts or badges on labels), plus one set apart at the far right:
 *
 *   **Analytics · Overview · Config · Interface · Testing · Runs · Releases**
 *   ⟨spacer⟩ **⟨/⟩ Developer Info**
 *
 * The order states the page's logic left to right: **how the service is doing**
 * (Analytics), what it **is and promises** (Overview, Interface), **how it is
 * built** (Config), and how it is **exercised and shipped** (Testing, Runs,
 * Releases). Analytics (task 1255) is behind `innovate.fe.analytics-tab`: the
 * service's runs, duration, cost, errors and criteria pass rate over time,
 * annotated with its publish and release events. Task 1327 moved it to the
 * front of the row and made it the landing tab, behind
 * `innovate.fe.services.analytics-first`; off, Analytics sits between Runs and
 * Releases (its `TAB_NAMES` position) and Overview is first again.
 *
 * The landing tab — where a bare `/services/:id`, an unknown `?tab=`, or a
 * `?tab=` naming a flagged-off tab arrives — is the FIRST VISIBLE TAB:
 * Analytics when its flag is on (and analytics-first is on), else Overview.
 * Runs (task 1306) sits after Testing behind `innovate.fe.services.runs-tab`:
 * every run of the service, newest first, test runs flagged, each opening the
 * one run page (`/services/:id/runs/:collaborationId`).
 *
 * Input/Output was a sixth product tab until task 882. Its two schema cards
 * are the service's contract rather than a subject of their own, so they moved
 * onto Overview below the Request card — editable exactly as they were — and
 * the tab name became a permanent alias.
 *
 * The gap before Developer Info is the design, not spacing (task 811): the
 * tabs on the left are for the person who OWNS the service, and the one on the
 * right is for the person they hand integration to. The muted styling and the
 * code glyph say the same thing the placement does — a different audience's
 * door, not another product tab. It is behind
 * `innovate.fe.services.developer-tab`, baked enabled; off, the row is exactly
 * the product tabs it was, and every other tab keeps its index.
 *
 * What the restructure fixed. The interface builder was buried as the first
 * section inside Connect, a tab that conflated human consumers with code
 * consumers; it is now its own tab. Runs was a tab away from the tests that
 * produce runs; the two became one Testing tab, and task 1306 gave runs their
 * own tab again — a plain log of what ran — once tests were tests (task 1227)
 * and a run meant exactly one execution. Nothing answered "what is
 * released, where, and how do I connect to it" in one place; Releases does.
 * Agents folded into Config, since a definition and the agents it names are both
 * "how it is built".
 *
 * `?tab=` deep-links by NAME, never index. Every retired name is a permanent
 * alias (`TAB_ALIASES`) rather than a duplicate tab, so every existing link and
 * bookmark still lands somewhere sensible — including the Composer's post-build
 * redirect to `?tab=connect`, which now arrives on the interface it just saved.
 * The four from the restructure point at the tab that absorbed them; `artifacts`
 * (task 854) points at Overview, because artifacts belong to a Collaboration and
 * left this page rather than moving within it; `input-output` (task 882) points
 * at Overview because that is where its cards went. An alias REWRITES the query
 * param to the canonical name, so a shared URL propagates the new vocabulary.
 *
 * The chat sidebar from the mockup is deliberately absent (blocked on
 * per-service conversation persistence — separate report).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { CodeBracketIcon, PlayIcon, ShieldCheckIcon, StopIcon } from '@heroicons/react/24/outline';
import { useEndpoint } from '../hooks/useEndpoints';
import { useFlag } from '../hooks/useFeatureFlags';
import { ServiceInterfaceTab } from '../components/services/detail/ServiceInterfaceTab';
import { ServiceConfigTab } from '../components/services/detail/ServiceConfigTab';
import {
  ACCESS_MODE_LABELS,
  ACCESS_MODE_SENTENCES,
  serviceAccessMode,
} from '../components/services/detail/serviceAccess';
import { ServiceTestingTab } from '../components/services/detail/ServiceTestingTab';
import { ServiceRunsTable } from '../components/services/detail/ServiceRunsTable';
import { ServiceReleasesTab } from '../components/services/detail/ServiceReleasesTab';
import { ServiceAnalyticsTab } from '../components/services/detail/ServiceAnalyticsTab';
import { ServiceDeveloperTab } from '../components/services/detail/ServiceDeveloperTab';
import { ServiceOverviewTab } from '../components/services/detail/ServiceOverviewTab';
import { ServiceTitleEditor } from '../components/services/detail/ServiceTitleEditor';
import { normalizeServicePath } from '../components/services/detail/serviceSnippets';
import { endpointDisplayName } from '../utils/endpointLabel';
import { ResourceVersionControls } from '../components/versioning/ResourceVersionControls';
import { ServiceStopChip } from '../components/services/ServiceStopChip';
import { ServiceStopBanner } from '../components/services/detail/ServiceStopBanner';
import {
  ServiceStopDialogs,
  type ServiceStopDialogKind,
} from '../components/services/detail/ServiceStopDialogs';

// Addressable tabs: `?tab=` maps to a tab NAME so callers can deep-link by
// name regardless of tab order (same pattern as EndpointDetailPage). Because
// nothing addresses these tabs by index, the row could be reordered outright in
// task 800 without breaking a single caller.
const TAB_NAMES = [
  'overview',
  'config',
  'interface',
  'testing',
  'runs',
  'analytics',
  'releases',
  'developer',
] as const;
type TabName = (typeof TAB_NAMES)[number];

const TAB_LABELS: Record<TabName, string> = {
  overview: 'Overview',
  interface: 'Interface',
  config: 'Config',
  testing: 'Testing',
  runs: 'Runs',
  analytics: 'Analytics',
  releases: 'Releases',
  developer: 'Developer Info',
};

/**
 * Tabs that stand apart from the product group, in the order they appear after
 * the spacer. Kept as a set rather than "the last tab" so the rule is stated,
 * not inferred from position — a future tab appended to `TAB_NAMES` should not
 * silently inherit the separation and the muted styling.
 */
const AUDIENCE_SPLIT_TABS = new Set<TabName>(['developer']);

/**
 * Retired tab names and where they now live (task 800).
 *
 * Permanent, not transitional: `?tab=connect` is baked into the Composer's
 * post-build redirect and into every link and bookmark anyone has saved, and a
 * deep link that silently drops the reader on the landing tab is worse than no
 * deep link at all. `test` lands on Testing because that tab absorbed the test
 * surface. `runs` was an alias to Testing from task 800 until task 1306 gave
 * runs their own tab again; it is a canonical tab name now, so every saved
 * `?tab=runs` link lands on the Runs tab (and on the landing tab when its flag
 * is off, like any flagged tab). `agents` goes further than selecting Config: it also opens
 * Config's agents sub-view (task 803), so the old link shows what it named.
 *
 * `artifacts` (task 854) is the one alias with nowhere better to go: artifacts
 * are what a Collaboration produced, not a property of the AI Service, so the
 * tab was removed rather than moved. Overview is where a link to a service
 * belongs when the thing it named is no longer on this page — the account-wide
 * /artifacts page still holds the files themselves.
 *
 * `input-output` (task 882) is the opposite case and the reason this map is
 * worth keeping: the tab is gone but its subject is not. The request and
 * response schema cards render on Overview now, so the old link lands on the
 * surface that still edits exactly what it named.
 */
const TAB_ALIASES: Record<string, TabName> = {
  connect: 'interface',
  test: 'testing',
  agents: 'config',
  artifacts: 'overview',
  'input-output': 'overview',
};

/** The canonical name for a `?tab=` value, or null when it names no tab. */
function resolveTabName(raw: string | null): TabName | null {
  if (!raw) return null;
  if ((TAB_NAMES as readonly string[]).includes(raw)) return raw as TabName;
  return TAB_ALIASES[raw] ?? null;
}

/**
 * The row position of a `?tab=` value, among the tabs actually RENDERED.
 *
 * Indexing against the visible list rather than `TAB_NAMES` is what lets a tab
 * be flag-gated without shifting its neighbours: with Developer Info off, the
 * six product tabs keep the indices they have always had, and `?tab=developer`
 * resolves to a tab that is not on the row — which falls back to the landing
 * tab (index 0, the first visible tab), exactly as an unknown name does.
 * Landing somewhere real beats rendering a panel the row cannot select.
 */
function tabNameToIndex(name: string | null, visible: readonly TabName[]): number {
  const resolved = resolveTabName(name);
  if (!resolved) return 0;
  const index = visible.indexOf(resolved);
  return index === -1 ? 0 : index;
}

export default function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: endpoint, isLoading, error } = useEndpoint(id);
  /*
   * Delete is not a header action (task 1042). It lives in a Danger Zone at the
   * foot of the Overview tab (`ServiceOverviewTab`), which owns the flag check,
   * the confirmation dialog, the mutation, the toast and the navigation back to
   * the list. Task 863 had put it here beside Publish so every tab could reach
   * it; kv's call reversed that — a destructive action belongs in the
   * conventional place for destructive settings, not next to the page's primary
   * action on every tab. The header "Open interface" button went in the same
   * task: it only selected the Interface tab, one click away on the rail below.
   */
  // A rollback lever, baked enabled (task 811): off returns the row to its six
  // product tabs. Note the call section moved OFF Releases, so turning this off
  // takes the call snippets off the page entirely — an incident action.
  const developerTabEnabled = useFlag('innovate.fe.services.developer-tab');
  /**
   * One lever per product tab (task 1216), on the Developer Info pattern: off
   * removes the tab from the row and its `?tab=` (aliases included) lands on
   * the landing tab. Overview has no tab flag of its own; it stays with the
   * page flag. The landing tab is the first visible tab: Analytics when its
   * flag is on, else Overview (task 1327).
   */
  const configTabEnabled = useFlag('innovate.fe.services.config-tab');
  const interfaceTabEnabled = useFlag('innovate.fe.services.interface-tab');
  const testingTabEnabled = useFlag('innovate.fe.services.testing-tab');
  const releasesTabEnabled = useFlag('innovate.fe.services.releases-tab');
  /**
   * The Analytics tab (task 1255), its rollback lever: off, the tab is not on
   * the row, `?tab=analytics` lands on Overview, and the series are never read
   * (the panel only mounts while its tab is rendered and selected).
   */
  const analyticsTabEnabled = useFlag('innovate.fe.analytics-tab');
  /**
   * The Runs tab (task 1306), its rollback lever: off, the tab is not on the
   * row and `?tab=runs` lands on the landing tab, not Testing.
   */
  const runsTabEnabled = useFlag('innovate.fe.services.runs-tab');
  /**
   * Analytics first (task 1327), its rollback lever: on, Analytics leads the
   * row and is the landing tab; off, the row is `TAB_NAMES` order exactly as
   * before and Overview is first and the landing tab.
   */
  const analyticsFirst = useFlag('innovate.fe.services.analytics-first');
  /**
   * Stop and Start (task 1216): the header actions, the dialogs, the Stopped
   * chip and the banner. The chip, banner and dialogs read the flag
   * themselves, so the AI Services list honors it too.
   */
  const stopEnabled = useFlag('innovate.fe.services.stop');
  /**
   * The two header chips (task 1022), each its own lever, both baked DISABLED
   * — kv's explicit call, an exception to the default-enabled rule.
   *
   * For normal work the access badge and the method+path chip are noise beside
   * the title; they may return as advanced-settings material later, so they are
   * flagged rather than deleted and either can come back from Unleash without a
   * deploy. Nothing becomes unreachable with them off: Config > Access is still
   * one click away on the tab rail (the badge was only a shortcut to it), and
   * the path is still shown in Developer Info and the Overview path editor.
   */
  const accessBadgeEnabled = useFlag('innovate.fe.services.access-badge');
  const pathChipEnabled = useFlag('innovate.fe.services.path-chip');

  const visibleTabs = useMemo<readonly TabName[]>(() => {
    const enabled: Record<TabName, boolean> = {
      overview: true,
      config: configTabEnabled,
      interface: interfaceTabEnabled,
      testing: testingTabEnabled,
      runs: runsTabEnabled,
      analytics: analyticsTabEnabled,
      releases: releasesTabEnabled,
      developer: developerTabEnabled,
    };
    const visible = TAB_NAMES.filter(name => enabled[name]);
    if (!analyticsFirst || !visible.includes('analytics')) return visible;
    return ['analytics', ...visible.filter(name => name !== 'analytics')];
  }, [
    configTabEnabled,
    interfaceTabEnabled,
    testingTabEnabled,
    runsTabEnabled,
    analyticsTabEnabled,
    releasesTabEnabled,
    developerTabEnabled,
    analyticsFirst,
  ]);

  const selectedTabIndex = tabNameToIndex(searchParams.get('tab'), visibleTabs);
  /**
   * What the reader actually ARRIVED with, captured once (task 803).
   *
   * The alias rewrite below replaces `?tab=agents` with `?tab=config`, which
   * loses the one thing that link was asking for. Config has sub-views now, so
   * the alias has to carry the reader all the way to the agents one — landing
   * on the graph editor instead would make the old link technically resolved
   * and practically wrong.
   */
  const [arrivedWithTab] = useState(() => searchParams.get('tab'));
  /** Which stop confirmation is open — from the header action or the banner (task 1164). */
  const [stopDialog, setStopDialog] = useState<ServiceStopDialogKind | null>(null);
  // Sync the selected tab into the URL, preserving all other query params.
  const setSelectedTabIndex = useCallback(
    (index: number) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('tab', visibleTabs[index] ?? visibleTabs[0]);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, visibleTabs]
  );

  /**
   * Open the Config tab's Access section — the header badge's action and the
   * destination the Interface tab's publishing card links to (task 973).
   *
   * One URL write rather than two, so the tab and its sub-view land together:
   * `?tab=config&section=access`. The sub-view param is the Config tab's own
   * (it reads and writes `section`); this page only has to say where to go.
   */
  const openAccessSection = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'config');
        next.set('section', 'access');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  /**
   * Rewrite a retired tab name to its canonical one, in place.
   *
   * A redirect rather than a second render path: the alias selects the right
   * tab immediately (via `tabNameToIndex`), and this replaces the URL so the
   * address bar — and anything copied out of it — carries the new vocabulary.
   * `replace` keeps the alias out of history, so Back does not bounce through it.
   */
  const rawTab = searchParams.get('tab');
  const aliasTarget = rawTab && !((TAB_NAMES as readonly string[]).includes(rawTab))
    ? TAB_ALIASES[rawTab]
    : undefined;

  useEffect(() => {
    if (!aliasTarget) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', aliasTarget);
        return next;
      },
      { replace: true }
    );
  }, [aliasTarget, setSearchParams]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading AI Service…</div>
      </div>
    );
  }

  if (error || !endpoint) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading AI Service. Please try again.
        </div>
      </div>
    );
  }

  // `name → description → method + path` (task 784), the same rule every list,
  // chip and picker in the app resolves labels through.
  const title = endpointDisplayName(endpoint);
  // Fails closed the way the service's own parser does: no policy means `jwt`.
  const accessMode = serviceAccessMode(endpoint);

  return (
    <div>
      {/* Header: the service name (inline-editable), access badge, method+path
          chip, actions. The chip is what carries the callable, slug-qualified
          path — the title is a label, not an address. The green "Live" badge
          that used to sit beside the title is gone (task 979): it was
          hardcoded, so it said the same thing on every AI Service, and the
          platform's vocabulary is published, released, serving.

          Both chips are flag-gated and baked OFF (task 1022): they read as
          technical noise next to the name for the everyday reader, so the
          default header is the title and the actions. Each flag is independent,
          so either chip can be turned back on from Unleash on its own. */}
      <div className="pb-5 border-b border-gray-200 dark:border-gray-700 sm:flex sm:items-start sm:justify-between">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <ServiceTitleEditor endpoint={endpoint} />
          {/* Stopped / Stopped automatically (task 1164); nothing while it takes calls. */}
          <ServiceStopChip stop={endpoint.stop} />
          {/* Who may CALL it, beside the title (task 973). A service built by
              the Composer is public from the moment it exists, and until this
              badge nothing on the page said so. Clicking it opens the Config
              tab's Access section, where it can be changed — a shortcut, not
              the only route, which is why hiding it (task 1022) is safe. */}
          {accessBadgeEnabled && (
            <button
              type="button"
              data-testid="service-access-badge"
              data-mode={accessMode}
              title={ACCESS_MODE_SENTENCES[accessMode]}
              onClick={openAccessSection}
              className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              <ShieldCheckIcon className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {ACCESS_MODE_LABELS[accessMode]}
            </button>
          )}
          {pathChipEnabled && (
            <span
              data-testid="service-path-chip"
              className="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700 px-2.5 py-1 font-mono text-xs text-gray-700 dark:text-gray-300"
            >
              {endpoint.method} {normalizeServicePath(endpoint)}
            </span>
          )}
        </div>
        <div className="mt-3 sm:ml-4 sm:mt-0 flex shrink-0 items-center gap-2">
          {/* Stop beside Publish, Start (green) while a stop is in force
              (task 1164; labelled Start by task 1191, the route stays
              /resume). Not in the Danger Zone: a stop is reversible and
              loses nothing. */}
          {!stopEnabled ? null : endpoint.stop ? (
            <button
              type="button"
              data-testid="service-resume-action"
              onClick={() => setStopDialog('resume')}
              className="inline-flex shrink-0 items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <PlayIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
              Start
            </button>
          ) : (
            <button
              type="button"
              data-testid="service-stop-action"
              onClick={() => setStopDialog('stop')}
              className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <StopIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
              Stop
            </button>
          )}
          {/* Version status + Publish (tasks 725, 782). These live in the
              header's action area rather than on a row of their own; the chip
              is read-only (it opens version history) and its quiet styling is
              what keeps it from reading as a second button beside Publish.
              Accounts that have not adopted Publishing & versions get no
              version status at all (task 979), and the component returns
              nothing when the feature is off — this is a flex row, so neither
              case leaves a gap. */}
          <ResourceVersionControls
            entityType="endpoint"
            entityId={endpoint.id}
            resourceName={title}
            className="shrink-0"
          />
        </div>
      </div>

      {/* Under the header on every tab while a stop is in force (task 1164). */}
      <ServiceStopBanner endpoint={endpoint} onResume={() => setStopDialog('resume')} />
      <ServiceStopDialogs endpoint={endpoint} open={stopDialog} onClose={() => setStopDialog(null)} />

      <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
        {/* Quiet tab row: labels only, no counts or badges (approved design).
            The one break in the row is the spacer before Developer Info, which
            is what makes a single nav line read as two audiences (task 811). */}
        <Tab.List className="mt-6 flex space-x-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {visibleTabs.map((name, index) => {
            const setApart = AUDIENCE_SPLIT_TABS.has(name);
            // The spacer goes before the FIRST set-apart tab only, so a second
            // one would join the same group rather than start a third.
            const opensTheSplit =
              setApart && (index === 0 || !AUDIENCE_SPLIT_TABS.has(visibleTabs[index - 1]));

            return (
              <Fragment key={name}>
                {opensTheSplit && <div className="flex-1" aria-hidden="true" />}
                <Tab
                  data-testid={`service-tab-${name}`}
                  className={({ selected }) =>
                    `whitespace-nowrap px-4 py-2 text-sm font-medium focus:outline-none ${
                      selected
                        ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                        : setApart
                          ? // A shade quieter than the product tabs: this door
                            // is for a different reader, and it should not
                            // compete with the six that are for this one.
                            'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`
                  }
                >
                  {setApart && <CodeBracketIcon className="mr-1 inline h-4 w-4" />}
                  {TAB_LABELS[name]}
                </Tab>
              </Fragment>
            );
          })}
        </Tab.List>
        <Tab.Panels className="mt-6">
          {/* One panel per RENDERED tab, in the same order (task 1216): the
              panel list has to match the tab list exactly or every index after
              a gated tab would shift. */}
          {visibleTabs.map(name => (
            <Tab.Panel key={name}>
              {name === 'overview' && (
                // Owns its own Edit/Save/Cancel (task 784) and, since task 882,
                // carries the two schema cards below it, each on its own
                // per-card save. Since task 1042 it also ends in the Danger
                // Zone that holds Delete.
                <ServiceOverviewTab endpoint={endpoint} />
              )}
              {name === 'config' && (
                // Definition editor plus the agents dossier folded in beneath
                // it (task 800); the service id travels for the dossier's
                // per-agent run-history links (task 797).
                <ServiceConfigTab
                  collaborationConfigId={endpoint.collaborationConfigId}
                  serviceId={endpoint.id}
                  endpoint={endpoint}
                  initialSection={arrivedWithTab === 'agents' ? 'agents' : undefined}
                />
              )}
              {name === 'interface' && <ServiceInterfaceTab endpoint={endpoint} />}
              {name === 'testing' && <ServiceTestingTab endpoint={endpoint} />}
              {name === 'runs' && <ServiceRunsTable endpointId={endpoint.id} />}
              {name === 'analytics' && <ServiceAnalyticsTab endpoint={endpoint} />}
              {name === 'releases' && <ServiceReleasesTab endpoint={endpoint} resourceName={title} />}
              {name === 'developer' && <ServiceDeveloperTab endpoint={endpoint} resourceName={title} />}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
